import type { GstOverrideType, PosGstLineBreakdown } from "../types";

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function splitCgstSgst(totalGst: number): { cgst: number; sgst: number } {
  if (totalGst <= 0) return { cgst: 0, sgst: 0 };
  const cgst = round2(totalGst / 2);
  const sgst = round2(totalGst - cgst);

  return { cgst, sgst };
}

/**
 * Mirrors backend GSTCalculatorService for POS display (server is authoritative at checkout).
 */
export function computePosGstLine(input: {
  fifoLineTotal: number;
  isGstApplicable: boolean;
  gstType: "inclusive" | "exclusive" | null | undefined;
  productGstRate: number | null | undefined;
  gst_override_type?: GstOverrideType | string | null;
  gst_override_value?: number | string | null;
}): PosGstLineBreakdown {
  const base = round2(input.fifoLineTotal);

  if (!input.isGstApplicable) {
    return {
      taxable: base,
      gst: 0,
      cgst: 0,
      sgst: 0,
      total: base,
      effectiveRatePercent: null,
      isCompliant: true,
    };
  }

  const overrideRaw = (input.gst_override_type ?? "none") as string;
  const override: GstOverrideType =
    overrideRaw === "manual_rate" || overrideRaw === "no_tax" ? (overrideRaw as GstOverrideType) : "none";

  if (override === "no_tax") {
    return {
      taxable: base,
      gst: 0,
      cgst: 0,
      sgst: 0,
      total: base,
      effectiveRatePercent: 0,
      isCompliant: false,
    };
  }

  const gstType = input.gstType ?? "exclusive";
  let effectiveRate: number | null = null;
  if (override === "manual_rate") {
    const v = input.gst_override_value;
    const n = typeof v === "string" ? Number.parseFloat(v) : typeof v === "number" ? v : Number.NaN;
    effectiveRate = Number.isFinite(n) ? round2(n) : null;
  } else {
    const r = input.productGstRate;
    effectiveRate = r != null && Number.isFinite(r) ? round2(r as number) : null;
  }

  if (effectiveRate === null || effectiveRate <= 0) {
    return {
      taxable: base,
      gst: 0,
      cgst: 0,
      sgst: 0,
      total: base,
      effectiveRatePercent: effectiveRate,
      isCompliant: override === "none",
    };
  }

  const compliant = override === "none";

  let taxable: number;
  let gst: number;
  let total: number;

  if (gstType === "exclusive") {
    taxable = base;
    gst = round2((taxable * effectiveRate) / 100);
    total = round2(taxable + gst);
  } else {
    total = base;
    taxable = round2(total / (1 + effectiveRate / 100));
    gst = round2(total - taxable);
    if (gst < 0) {
      gst = 0;
      taxable = total;
    }
  }

  const { cgst, sgst } = splitCgstSgst(gst);

  return {
    taxable,
    gst,
    cgst,
    sgst,
    total,
    effectiveRatePercent: effectiveRate,
    isCompliant: compliant,
  };
}

export function cartHasNonCompliantGst(items: Array<{ gst: PosGstLineBreakdown }>): boolean {
  return items.some((i) => !i.gst.isCompliant);
}
